/**
 * Painel de ferramentas do agente POR CONVERSA (header do chat).
 *
 * Um botão de ajustes abre um diálogo listando o catálogo de ferramentas
 * (`AGENT_TOOL_CATALOG`, fonte única dos rótulos pt-BR) com um switch para
 * habilitar/desabilitar cada uma NESTA conversa. O estado persiste no campo
 * `disabledTools` do doc da conversa (`setConversationDisabledTools`); o backend
 * relê a cada turno, então a mudança vale para as próximas mensagens. Só é
 * editável com uma conversa ativa — em conversa nova o botão fica desabilitado
 * (o doc ainda não existe).
 *
 * ESTADO OTIMISTA: os switches refletem um estado local que atualiza na hora do
 * clique (via ref síncrona, para toggles rápidos em sequência não perderem a
 * alteração anterior enquanto o snapshot do Firestore não voltou). O doc
 * (prop `disabledTools`) ressincroniza o estado local ao trocar de conversa e
 * sempre que não houver escrita em voo; em erro de escrita, reverte ao doc.
 */
import { AGENT_TOOL_CATALOG, isToolEnabled } from '@agente/shared'
import { SlidersHorizontal } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { useAuth } from '@/lib/auth'
import { setConversationDisabledTools } from '@/lib/firestore'

/** Igualdade por conteúdo (ordem-insensível) entre duas listas de ids. */
function sameMembers(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false
  const set = new Set(a)
  return b.every((item) => set.has(item))
}

export function ToolsPanel({
  conversationId,
  disabledTools,
}: {
  conversationId: string | null
  /** Ferramentas desabilitadas na conversa ativa (do doc da conversa). */
  disabledTools: string[]
}) {
  const { user } = useAuth()
  const uid = user?.uid ?? null

  // Estado otimista local + ref com o último valor conhecido (a ref atualiza de
  // forma SÍNCRONA no toggle, então dois cliques rápidos compõem em vez de o
  // segundo partir de um estado defasado e descartar o primeiro).
  const [localDisabled, setLocalDisabled] = useState<string[]>(disabledTools)
  const localDisabledRef = useRef<string[]>(disabledTools)
  const pendingWritesRef = useRef(0)
  const prevConversationRef = useRef(conversationId)

  // Ressincroniza com o doc: sempre ao trocar de conversa (estado por conversa
  // correto) e, na mesma conversa, apenas quando não há escrita em voo — para o
  // snapshot atrasado não sobrescrever um toggle otimista ainda não confirmado.
  // O bail-out por conteúdo (sameMembers) evita re-renders com a prop recriada
  // a cada render do pai.
  useEffect(() => {
    const switched = prevConversationRef.current !== conversationId
    prevConversationRef.current = conversationId
    if (!switched && pendingWritesRef.current > 0) return
    if (switched || !sameMembers(localDisabledRef.current, disabledTools)) {
      localDisabledRef.current = disabledTools
      setLocalDisabled(disabledTools)
    }
  }, [conversationId, disabledTools])

  const enabledCount = AGENT_TOOL_CATALOG.filter((t) => isToolEnabled(t.id, localDisabled)).length

  async function toggleTool(id: string, enabled: boolean) {
    if (!uid || !conversationId) return
    const current = localDisabledRef.current
    const next = enabled ? current.filter((t) => t !== id) : Array.from(new Set([...current, id]))
    // Otimista: ref (síncrona) + estado (render) antes do round-trip.
    localDisabledRef.current = next
    setLocalDisabled(next)
    pendingWritesRef.current += 1
    try {
      await setConversationDisabledTools(uid, conversationId, next)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível atualizar as ferramentas', { description: message })
      // Reverte ao último valor do doc (a prop) — o snapshot é a fonte da verdade.
      localDisabledRef.current = disabledTools
      setLocalDisabled(disabledTools)
    } finally {
      pendingWritesRef.current -= 1
    }
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Ferramentas do agente"
          disabled={!conversationId}
        >
          <SlidersHorizontal className="size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Ferramentas do agente</DialogTitle>
          <DialogDescription>
            Escolha quais ferramentas o agente pode usar nesta conversa. Vale para as próximas
            mensagens. {enabledCount} de {AGENT_TOOL_CATALOG.length} habilitadas.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1">
          {AGENT_TOOL_CATALOG.map((tool) => {
            const enabled = isToolEnabled(tool.id, localDisabled)
            return (
              <label
                key={tool.id}
                htmlFor={`tool-${tool.id}`}
                className="hover:bg-accent flex cursor-pointer items-start justify-between gap-3 rounded-lg px-2 py-2.5 transition-colors"
              >
                <div className="min-w-0 space-y-0.5">
                  <p className="text-sm font-medium">{tool.label}</p>
                  <p className="text-muted-foreground text-xs">{tool.description}</p>
                </div>
                <Switch
                  id={`tool-${tool.id}`}
                  checked={enabled}
                  onCheckedChange={(next) => void toggleTool(tool.id, next)}
                  className="mt-0.5 shrink-0"
                  aria-label={`${tool.label}: ${enabled ? 'habilitada' : 'desabilitada'}`}
                />
              </label>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}
